import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { InventoryMatchingStatus } from '@ekhodealer/ekho-common';
import { SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';

import type { FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';
import type { VehicleMatchOption } from '../../../atoms/inventoryAtoms';

import { AccordionSection } from '@common/components/AccordionSection';
import { FiltersDrawerCheckboxGroup } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { updateVehicleMatchFilterAtom,vehicleMatchFilterAtom } from '../../../atoms/inventoryAtoms';
import { useInventoryFilterFacetCounts } from '../../../hooks/useInventoryFilterFacetCounts';


const VEHICLE_MATCH_OPTIONS = [
	{ value: 'in-progress', label: 'In progress' },
	{ value: 'complete', label: 'Complete' },
];

/**
 * Maps backend matching.status enum values to frontend vehicleMatch values
 * in-progress -> ENRICHED, VEHICLE_MATCHED, OEM_MATCHED (sum of all)
 * complete -> MATCHED
 */
const getVehicleMatchCount = (
	matchingStatusCounts: Record<string, number>,
	frontendValue: string,
): number => {
	switch (frontendValue) {
		case 'in-progress':
			return (
				(matchingStatusCounts[InventoryMatchingStatus.ENRICHED] || 0) +
				(matchingStatusCounts[InventoryMatchingStatus.VEHICLE_MATCHED] || 0) +
				(matchingStatusCounts[InventoryMatchingStatus.OEM_MATCHED] || 0)
			);
		case 'complete':
			return matchingStatusCounts[InventoryMatchingStatus.MATCHED] || 0;
		default:
			return 0;
	}
};

export function MatchStatusSection() {
	const vehicleMatch = useAtomValue(vehicleMatchFilterAtom);
	const setVehicleMatch = useSetAtom(updateVehicleMatchFilterAtom);

	// Get facet counts from the API response
	const { matchingStatusCounts, isLoading } = useInventoryFilterFacetCounts();

	// Build vehicle match items with counts from facets
	const vehicleMatchItems: FiltersDrawerCheckboxGroupItem[] = useMemo(() => {
		return VEHICLE_MATCH_OPTIONS.map((option) => ({
			value: option.value,
			label: option.label,
			count: getVehicleMatchCount(matchingStatusCounts, option.value),
		}));
	}, [matchingStatusCounts]);

	// Wrapper function to convert string[] to VehicleMatchOption[]
	const handleChange = (values: string[]) => {
		setVehicleMatch(values as VehicleMatchOption[]);
	};

	return (
		<AccordionSection title='Vehicle match' defaultExpanded>
			{isLoading ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='100%' height='24px' />
					<SkeletonRect width='100%' height='24px' />
				</Stack>
			) : (
				<FiltersDrawerCheckboxGroup
					items={vehicleMatchItems}
					value={vehicleMatch}
					onChange={handleChange}
				/>
			)}
		</AccordionSection>
	);
}
